import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../AppContext";
import {
  Search,
  X,
  FileText,
  Calendar,
  Building,
  Receipt,
  DollarSign,
  CheckSquare,
  Square,
  Download,
  Trash2,
  Maximize2,
  AlertCircle,
  Plus,
} from "lucide-react";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../../App";
import BossCreateInvoice from "./BossInvoicesComps/BossCreateInvoice";
import BossModalShowInvoice from "./BossStatisticComps/BossModalShowInvoice";
import MPBossInvoices from "./BossInvoicesComps/MPBossInvoices";

// ── Helpers ───────────────────────────────────────────────────────────────────
const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size)
    chunks.push(arr.slice(i, i + size));
  return chunks;
};

const parseDateParts = (dateStr) => {
  if (!dateStr || dateStr.trim() === "") return null;
  const sep = dateStr.includes("/") ? "/" : dateStr.includes("-") ? "-" : null;
  if (!sep) return null;
  const parts = dateStr.split(sep);
  if (parts.length !== 3) return null;
  return parts;
};

const CURRENCY_STYLES = {
  ILS: {
    border: "border-blue-500",
    text: "text-blue-500",
    bg: "bg-blue-50",
    icon: "text-blue-500",
    symbol: "₪",
    name: "Shekel (ILS)",
  },
  USD: {
    border: "border-green-500",
    text: "text-green-500",
    bg: "bg-green-50",
    icon: "text-green-500",
    symbol: "$",
    name: "Dollar (USD)",
  },
  EUR: {
    border: "border-yellow-500",
    text: "text-yellow-500",
    bg: "bg-yellow-50",
    icon: "text-yellow-500",
    symbol: "€",
    name: "Euro (EUR)",
  },
  GBP: {
    border: "border-purple-500",
    text: "text-purple-500",
    bg: "bg-purple-50",
    icon: "text-purple-500",
    symbol: "£",
    name: "Pound (GBP)",
  },
};

const getCurrencySymbol = (code) => CURRENCY_STYLES[code]?.symbol || "₪";

function mapEmailInvoice(inv) {
  return {
    id: inv.id,
    dbId: inv.id,
    source: inv.source || "manual",
    inoviceNumber: inv.invoiceNumber || null,
    inoviceDate: inv.date || "",
    vendorName: inv.whoSend || inv.subject || "",
    totalAmount: inv.price || null,
    currency: inv.currency || "ILS",
    description: inv.description || null,
    hasAttachment: inv.hasAttachment || false,
    attachmentId: inv.attachmentId || null,
    attachmentName: inv.attachmentName || null,
    messageId: inv.messageId || null,
    score: inv.score || 0,
    image:
      inv.source === "manual" && inv.attachmentId ? inv.attachmentId : null,
  };
}

const parseDate = (s) => {
  if (!s) return null;
  const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m1) return new Date(+m1[3], +m1[2] - 1, +m1[1]);
  const m2 = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m2) return new Date(+m2[3], +m2[2] - 1, +m2[1]);
  const d = new Date(s);
  return isNaN(d) ? null : d;
};

const parseAmount = (str) => {
  if (!str) return 0;
  const n = parseFloat(String(str).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
};

const groupByCurrency = (invoiceList) => {
  const groups = {};
  invoiceList.forEach((inv) => {
    if (!inv.totalAmount) return;
    const amount = parseAmount(inv.totalAmount);
    if (!amount || amount <= 0) return;
    const code = inv.currency || "ILS";
    if (!groups[code]) groups[code] = { sum: 0, count: 0 };
    groups[code].sum += amount;
    groups[code].count += 1;
  });
  return groups;
};

const SOURCE_STYLES = {
  gmail: { bg: "#fff1f2", color: "#be123c", label: "Gmail" },
  outlook: { bg: "#eff6ff", color: "#1d4ed8", label: "Outlook" },
  manual: { bg: "#f5f3ff", color: "#6d28d9", label: "Manual" },
};
const SOURCE_LABEL = { gmail: "Gmail", outlook: "Outlook", manual: "Manual" };

export default function BossInvoices() {
  const { t } = useTranslation();
  const { Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";
  const [activeTab, setActiveTab] = useState("pull");
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // ── ALL-TIME totals state ─────────────────────────────────────────────────
  const [allTimeInvoices, setAllTimeInvoices] = useState([]);
  const [loadingAllTime, setLoadingAllTime] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const [createInvoiceVisible, setCreateInvoiceVisible] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedInvoiceImage, setSelectedInvoiceImage] = useState(null);

  const [showExportMenu, setShowExportMenu] = useState(false);

  const API = MyDomain.endsWith("/") ? MyDomain.slice(0, -1) : MyDomain;

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    const now = new Date();
    const defaultMonth = `${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
    const month = localStorage.getItem("choosedMonth") || defaultMonth;
    if (data) setAllData(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
    setChoosedMonth(month);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (choosedMonth) localStorage.setItem("choosedMonth", choosedMonth);
  }, [choosedMonth]);

  // ── Load all-time invoices (last 3 years) ─────────────────────────────────
  const loadAllTimeInvoices = useCallback(
    async (userId) => {
      const uid = userId || userData?.id;
      if (!uid) return;
      setLoadingAllTime(true);
      try {
        const now = new Date();
        const startDate = `${now.getFullYear() - 3}/01/01`;
        const endDate = `${now.getFullYear()}/12/31`;
        const res = await fetch(
          `${API}/api/email-invoices/${uid}?startDate=${startDate}&endDate=${endDate}`,
        );
        if (res.ok) {
          const d = await res.json();
          const mapped = (d.invoices || []).map(mapEmailInvoice);
          setAllTimeInvoices(mapped);
        }
      } catch (err) {
        console.error("❌ Error loading all-time invoices:", err);
      }
      setLoadingAllTime(false);
    },
    [API, userData?.id],
  );

  // ── Load selected month invoices ──────────────────────────────────────────
  const loadEmailInvoices = useCallback(
    async (month, userId) => {
      const uid = userId || userData?.id;
      if (!uid || !month) return;
      setLoadingInvoices(true);
      try {
        const [mm, yyyy] = month.split("-");
        const startDate = `${yyyy}/${mm}/01`;
        const lastDay = new Date(parseInt(yyyy), parseInt(mm), 0).getDate();
        const endDate = `${yyyy}/${mm}/${String(lastDay).padStart(2, "0")}`;

        const res = await fetch(
          `${API}/api/email-invoices/${uid}?startDate=${startDate}&endDate=${endDate}`,
        );

        if (res.ok) {
          const d = await res.json();
          const mapped = (d.invoices || []).map(mapEmailInvoice);
          mapped.sort(
            (a, b) =>
              (parseDate(b.inoviceDate) || 0) - (parseDate(a.inoviceDate) || 0),
          );
          setInvoices(mapped);
        }
      } catch (err) {
        console.error("❌ Error loading invoices:", err);
      }
      setLoadingInvoices(false);
    },
    [API, userData?.id],
  );

  // Load both when ready
  useEffect(() => {
    if (userData?.id && choosedMonth) {
      loadEmailInvoices(choosedMonth, userData.id);
      loadAllTimeInvoices(userData.id);
    }
  }, [userData?.id, choosedMonth]);

  if (loading || !allData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Month list ────────────────────────────────────────────────────────────
  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(
        `${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`,
      );
    }
    return months;
  };
  const monthList = generateMonths();

  // ── All-time totals ───────────────────────────────────────────────────────
  const allTimeCurrencyGroups = groupByCurrency(allTimeInvoices);
  const allTimeTotalCount = allTimeInvoices.length;

  // ── Filter by selected month ──────────────────────────────────────────────
  const filteredInvoices = invoices.filter((inv) => {
    if (!choosedMonth) return false;
    if (!inv.inoviceDate || inv.inoviceDate.trim() === "") return false;
    const parts = parseDateParts(inv.inoviceDate);
    if (!parts) return false;
    const [, month, year] = parts;
    return `${month}-${year}` === choosedMonth;
  });

  const invoicesWithoutDates = invoices.filter(
    (inv) => !inv.inoviceDate || inv.inoviceDate.trim() === "",
  );

  const applySearch = (list) => {
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (inv) =>
        (inv.inoviceNumber || "").toLowerCase().includes(q) ||
        (inv.vendorName || "").toLowerCase().includes(q) ||
        (inv.description || "").toLowerCase().includes(q),
    );
  };

  const searchFilteredInvoices = applySearch(filteredInvoices);
  const searchFilteredWithoutDates = applySearch(invoicesWithoutDates);
  const displayInvoices = searchQuery.trim()
    ? searchFilteredInvoices
    : filteredInvoices;
  const totalInvoices = displayInvoices.length;
  const currencyGroups = groupByCurrency(displayInvoices);

  // ── Selection ─────────────────────────────────────────────────────────────
  const toggleSelectionMode = () => {
    setSelectionMode((p) => !p);
    setSelectedInvoices([]);
  };
  const toggleInvoiceSelection = (id) =>
    setSelectedInvoices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const selectAllInvoices = () =>
    setSelectedInvoices(searchFilteredInvoices.map((inv) => inv.id));
  const deselectAllInvoices = () => setSelectedInvoices([]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(t("noInvoice"))) return;
    try {
      await fetch(`${API}/api/email-invoices/${userData.id}/${invoice.dbId}`, {
        method: "DELETE",
      });
      setInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
      setAllTimeInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
    } catch (error) {
      console.error("❌ Error deleting invoice:", error);
      alert(t("error"));
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const getDataToExport = () =>
    selectedInvoices.length > 0
      ? filteredInvoices.filter((inv) => selectedInvoices.includes(inv.id))
      : filteredInvoices;

  const exportToCSV = () => {
    setShowExportMenu(false);
    const data = getDataToExport();
    if (!data.length) {
      alert(t("noInvoicesToExport"));
      return;
    }
    const headers = [
      "Source",
      "Invoice Number",
      "Date",
      "Vendor",
      "Currency",
      "Amount",
      "Description",
    ];
    const rows = data.map((inv) => [
      SOURCE_LABEL[inv.source] || inv.source,
      inv.inoviceNumber || "",
      inv.inoviceDate || "",
      inv.vendorName || "",
      inv.currency || "ILS",
      inv.totalAmount || "0",
      inv.description || "",
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows]
        .map((r) => r.map((c) => `"${c ?? ""}`).join(","))
        .join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `invoices_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSelectionMode(false);
    setSelectedInvoices([]);
  };

  const exportToPDF = () => {
    setShowExportMenu(false);
    const data = getDataToExport();
    if (!data.length) {
      alert(t("noInvoicesToExport"));
      return;
    }
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body{font-family:Arial,sans-serif;padding:20px}
      h1{color:#8B5CF6;text-align:center;margin-bottom:10px}
      .subtitle{text-align:center;color:#6B7280;margin-bottom:30px}
      table{width:100%;border-collapse:collapse;margin-top:20px;font-size:11px}
      th,td{border:1px solid #ddd;padding:8px;text-align:left}
      th{background:#8B5CF6;color:#fff;font-weight:bold}
      .summary-box{background:#F9FAFB;padding:15px;border-radius:8px;margin-bottom:20px;border:1px solid #E5E7EB}
      .src-gmail{color:#be123c;font-weight:700}.src-outlook{color:#1d4ed8;font-weight:700}.src-manual{color:#6d28d9;font-weight:700}
    </style></head><body>
    <h1>Invoices</h1>
    <div class="subtitle">Month: ${choosedMonth} · Generated: ${new Date().toLocaleDateString()}</div>
    <div class="summary-box">
      <div><strong>Total Invoices:</strong> ${data.length}</div>
      ${Object.entries(currencyGroups)
        .map(
          ([code, { sum }]) =>
            `<div><strong>${CURRENCY_STYLES[code]?.name || code}:</strong> ${getCurrencySymbol(code)}${sum.toLocaleString("he-IL", { minimumFractionDigits: 2 })}</div>`,
        )
        .join("")}
    </div>
    <table><thead><tr>
      <th>Source</th><th>Invoice #</th><th>Date</th><th>Vendor</th><th>Currency</th><th>Amount</th><th>Description</th>
    </tr></thead><tbody>
    ${data
      .map(
        (inv) => `
      <tr>
        <td class="src-${inv.source}">${SOURCE_LABEL[inv.source] || inv.source}</td>
        <td>${inv.inoviceNumber || "-"}</td>
        <td>${inv.inoviceDate || "-"}</td>
        <td>${inv.vendorName || "-"}</td>
        <td>${inv.currency || "ILS"}</td>
        <td>${getCurrencySymbol(inv.currency || "ILS")}${parseAmount(inv.totalAmount).toLocaleString("he-IL", { minimumFractionDigits: 2 })}</td>
        <td>${inv.description || "-"}</td>
      </tr>`,
      )
      .join("")}
    </tbody></table>
    </body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 500);
    setSelectionMode(false);
    setSelectedInvoices([]);
  };

  // ── Invoice card ──────────────────────────────────────────────────────────
  const renderInvoiceCard = (invoice) => {
    const isSelected = selectedInvoices.includes(invoice.id);
    const ss = SOURCE_STYLES[invoice.source] || SOURCE_STYLES.manual;
    const sym = getCurrencySymbol(invoice.currency || "ILS");
    const amount = parseAmount(invoice.totalAmount);

    let formattedDate = "";
    if (invoice.inoviceDate) {
      const ds = invoice.inoviceDate.replace(/\//g, "-");
      const d = moment(ds, ["DD-MM-YYYY", "MM-DD-YYYY", "YYYY-MM-DD"], true);
      formattedDate = d.isValid()
        ? d.format("MMM DD, YYYY")
        : invoice.inoviceDate;
    }

    return (
      <div
        key={invoice.id}
        onClick={() => {
          if (selectionMode) toggleInvoiceSelection(invoice.id);
          else {
            setSelectedInvoice(invoice);
            setShowInvoiceModal(true);
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (!selectionMode) {
            setSelectionMode(true);
            toggleInvoiceSelection(invoice.id);
          }
        }}
        className={`relative rounded-xl p-2 border flex flex-col gap-1.5 cursor-pointer transition ${
          isSelected
            ? "bg-purple-50 border-purple-400 border-2"
            : "bg-gray-50 border-gray-200"
        }`}
        style={{
          flex: "1 1 calc(33.333% - 8px)",
          minWidth: 0,
          maxWidth: "calc(33.333% - 8px)",
        }}
      >
        {selectionMode && (
          <div className="absolute top-2 right-2 z-10">
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                isSelected
                  ? "bg-purple-500 border-purple-500"
                  : "bg-white border-gray-300"
              }`}
            >
              {isSelected && (
                <span className="text-white text-xs font-bold">✓</span>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-1 flex-wrap">
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ background: ss.bg, color: ss.color }}
          >
            {ss.label}
          </span>
          {invoice.inoviceNumber && (
            <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded truncate max-w-full">
              #{invoice.inoviceNumber}
            </span>
          )}
        </div>

        <p className="text-xs font-semibold text-gray-900 truncate leading-tight">
          {invoice.vendorName || t("vendorName")}
        </p>

        {formattedDate && (
          <div className="flex items-center gap-0.5">
            <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className="text-[9px] text-gray-500">{formattedDate}</span>
          </div>
        )}

        {invoice.image && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedInvoiceImage(invoice.image);
              setImageModalVisible(true);
            }}
            className="relative rounded-lg overflow-hidden"
          >
            <img
              src={invoice.image}
              alt="Invoice"
              className="w-full h-16 object-cover rounded-lg"
            />
            <div className="absolute bottom-1 right-1 w-5 h-5 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
              <Maximize2 className="w-2.5 h-2.5 text-white" />
            </div>
          </button>
        )}

        {invoice.hasAttachment && invoice.source !== "manual" && (
          <span className="text-[9px] bg-yellow-50 text-yellow-700 border border-yellow-200 px-1.5 py-0.5 rounded font-semibold self-start">
            📎 PDF
          </span>
        )}

        {invoice.totalAmount && amount > 0 && (
          <div className="flex items-center justify-between border-t border-gray-100 pt-1 mt-0.5">
            <span className="text-[9px] font-bold text-gray-700">
              {t("total")}
            </span>
            <span className="text-[10px] font-bold text-purple-600">
              {sym}
              {amount.toLocaleString("he-IL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        )}

        {!selectionMode && (
          <div className="flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteInvoice(invoice);
              }}
              className="p-1 hover:bg-red-50 rounded-lg transition"
            >
              <Trash2 className="w-3 h-3 text-red-400" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── All-time summary card renderer ────────────────────────────────────────
  const renderAllTimeCurrencyCard = () => {
    if (Object.keys(allTimeCurrencyGroups).length === 0) {
      return (
        <div className="bg-white rounded-xl px-3 py-2 shadow-sm border-l-4 border-blue-500 flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div>
            <p className="text-[9px] text-gray-400">{t("totalAmount")}</p>
            <p className="text-sm font-bold text-blue-500">₪0</p>
          </div>
        </div>
      );
    }

    if (Object.keys(allTimeCurrencyGroups).length === 1) {
      const [code, { sum, count }] = Object.entries(allTimeCurrencyGroups)[0];
      const style = CURRENCY_STYLES[code] || CURRENCY_STYLES.ILS;
      return (
        <div className={`bg-white rounded-xl px-3 py-2 shadow-sm border-l-4 ${style.border} flex items-center gap-2`}>
          <div className={`w-7 h-7 ${style.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
            <span className={`text-xs font-bold ${style.icon}`}>{style.symbol}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] text-gray-400">{t("totalAmount")}</p>
            <p className={`text-sm font-bold ${style.text} truncate`}>
              {style.symbol}{sum.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[9px] text-gray-400">{count} inv</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        {Object.entries(allTimeCurrencyGroups).map(([code, { sum, count }]) => {
          const style = CURRENCY_STYLES[code] || CURRENCY_STYLES.ILS;
          return (
            <div key={code} className={`bg-white rounded-lg px-2 py-1.5 shadow-sm border-l-4 ${style.border} flex items-center justify-between`}>
              <div className="flex items-center gap-1.5">
                <div className={`w-5 h-5 ${style.bg} rounded-full flex items-center justify-center`}>
                  <span className={`text-[9px] font-bold ${style.icon}`}>{style.symbol}</span>
                </div>
                <p className={`text-xs font-bold ${style.text}`}>
                  {style.symbol}{sum.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <span className="text-[9px] text-gray-400">{count}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Tab buttons */}
      <div className="px-4 pt-3 flex gap-2">
        <button
          onClick={() => setActiveTab("pull")}
          className={`flex-1 py-3 rounded-2xl font-semibold text-sm transition-all border-2 ${
            activeTab === "pull"
              ? "bg-purple-500 border-purple-500 text-white shadow-lg"
              : "bg-white border-gray-200 text-gray-600 hover:border-purple-300"
          }`}
        >
          {t("pullInvoices") || "Pull Invoices"}
        </button>
        <button
          onClick={() => setActiveTab("invoices")}
          className={`flex-1 py-3 rounded-2xl font-semibold text-sm transition-all border-2 ${
            activeTab === "invoices"
              ? "bg-purple-500 border-purple-500 text-white shadow-lg"
              : "bg-white border-gray-200 text-gray-600 hover:border-purple-300"
          }`}
        >
          {t("invoices") || "Invoices"}
        </button>
      </div>

      {activeTab === "pull" && (
        <div className="flex-1 h-full">
          <MPBossInvoices />
        </div>
      )}

      {activeTab === "invoices" && (
        <>
      {/* ── ALL-TIME Summary cards ── */}
      <div className="px-4 mt-3">
        <div className="flex items-center gap-1 mb-1.5">
          <h2 className="text-xs font-bold text-gray-700">{t("totalInvoices")}</h2>
          {loadingAllTime && (
            <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white rounded-xl px-3 py-2 shadow-sm border-l-4 border-purple-500 flex items-center gap-2">
            <div className="w-7 h-7 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-3.5 h-3.5 text-purple-500" />
            </div>
            <div>
              <p className="text-[9px] text-gray-400">{t("totalInvoices")}</p>
              <p className="text-sm font-bold text-gray-900">{allTimeTotalCount}</p>
            </div>
          </div>
          {renderAllTimeCurrencyCard()}
        </div>
      </div>

      {/* Month Selector */}
      <div className="px-4 pt-3">
        <div className="flex gap-2 overflow-x-auto pb-1.5 hide-scrollbar">
          {monthList.map((item) => {
            const [, year] = item.split("-");
            const monthName = moment(item, "MM-YYYY").format("MMM");
            const isSelected = choosedMonth === item;
            return (
              <button
                key={item}
                onClick={() => setChoosedMonth(item)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-xl border text-center transition-all ${
                  isSelected
                    ? "bg-purple-500 border-purple-500 shadow-md"
                    : "bg-white border-gray-200 hover:border-purple-300"
                }`}
              >
                <p
                  className={`text-xs font-semibold ${isSelected ? "text-white" : "text-gray-800"}`}
                >
                  {monthName}
                </p>
                <p
                  className={`text-[9px] ${isSelected ? "text-purple-100" : "text-gray-400"}`}
                >
                  {year.slice(2)}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Selected Month Summary cards ── */}
      <div className="px-4 mt-2">
        <h2 className="text-xs font-semibold text-gray-400 mb-1.5">
          {moment(choosedMonth, "MM-YYYY").format("MMMM YYYY")}
        </h2>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white rounded-xl px-3 py-2 shadow-sm border-l-4 border-purple-500 flex items-center gap-2">
            <div className="w-7 h-7 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-3.5 h-3.5 text-purple-500" />
            </div>
            <div>
              <p className="text-[9px] text-gray-400">{t("invoices")}</p>
              <p className="text-sm font-bold text-gray-900">{totalInvoices}</p>
            </div>
          </div>

          {Object.keys(currencyGroups).length === 0 ? (
            <div className="bg-white rounded-xl px-3 py-2 shadow-sm border-l-4 border-blue-500 flex items-center gap-2">
              <div className="w-7 h-7 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-3.5 h-3.5 text-blue-500" />
              </div>
              <div>
                <p className="text-[9px] text-gray-400">{t("totalAmount")}</p>
                <p className="text-sm font-bold text-blue-500">₪0</p>
              </div>
            </div>
          ) : Object.keys(currencyGroups).length === 1 ? (
            (() => {
              const [code, { sum, count }] = Object.entries(currencyGroups)[0];
              const style = CURRENCY_STYLES[code] || CURRENCY_STYLES.ILS;
              return (
                <div className={`bg-white rounded-xl px-3 py-2 shadow-sm border-l-4 ${style.border} flex items-center gap-2`}>
                  <div className={`w-7 h-7 ${style.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-xs font-bold ${style.icon}`}>{style.symbol}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[9px] text-gray-400">{t("totalAmount")}</p>
                    <p className={`text-sm font-bold ${style.text} truncate`}>
                      {style.symbol}{sum.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[9px] text-gray-400">{count} inv</p>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="flex flex-col gap-1">
              {Object.entries(currencyGroups).map(([code, { sum, count }]) => {
                const style = CURRENCY_STYLES[code] || CURRENCY_STYLES.ILS;
                return (
                  <div key={code} className={`bg-white rounded-lg px-2 py-1.5 shadow-sm border-l-4 ${style.border} flex items-center justify-between`}>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-5 h-5 ${style.bg} rounded-full flex items-center justify-center`}>
                        <span className={`text-[9px] font-bold ${style.icon}`}>{style.symbol}</span>
                      </div>
                      <p className={`text-xs font-bold ${style.text}`}>
                        {style.symbol}{sum.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <span className="text-[9px] text-gray-400">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 mt-3">
        <div className="flex items-center bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-200">
          <Search className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("searchInvoices")}
            className="flex-1 text-sm text-gray-800 outline-none bg-transparent"
          />
          {searchQuery.length > 0 && (
            <button
              onClick={() => setSearchQuery("")}
              className="ml-2 p-1 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Selection + Export bar */}
      <div className="px-4 mt-2 flex flex-wrap items-center gap-2">
        <button
          onClick={toggleSelectionMode}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-xs transition ${
            selectionMode
              ? "bg-red-50 text-red-500"
              : "bg-purple-50 text-purple-600"
          }`}
        >
          {selectionMode ? (
            <X className="w-3.5 h-3.5" />
          ) : (
            <CheckSquare className="w-3.5 h-3.5" />
          )}
          {selectionMode ? t("cancel") : t("select")}
        </button>

        {selectionMode && (
          <>
            <button
              onClick={
                selectedInvoices.length === searchFilteredInvoices.length
                  ? deselectAllInvoices
                  : selectAllInvoices
              }
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-50 text-purple-600 font-semibold text-xs"
            >
              {selectedInvoices.length === searchFilteredInvoices.length ? (
                <Square className="w-3.5 h-3.5" />
              ) : (
                <CheckSquare className="w-3.5 h-3.5" />
              )}
              {selectedInvoices.length === searchFilteredInvoices.length
                ? t("deselectAll")
                : t("selectAll")}
            </button>
            <div className="relative">
              <button
                onClick={() => setShowExportMenu((p) => !p)}
                disabled={selectedInvoices.length === 0}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-xs text-white transition ${
                  selectedInvoices.length === 0
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-purple-500 hover:bg-purple-600"
                }`}
              >
                <Download className="w-3.5 h-3.5" /> {t("export")} (
                {selectedInvoices.length})
              </button>
              {showExportMenu && selectedInvoices.length > 0 && (
                <div className="absolute left-0 top-11 bg-white rounded-xl shadow-lg border border-gray-100 z-50 min-w-[140px] overflow-hidden">
                  <button
                    onClick={exportToCSV}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <FileText className="w-4 h-4 text-green-500" />{" "}
                    {t("export")} CSV
                  </button>
                  <div className="h-px bg-gray-100" />
                  <button
                    onClick={exportToPDF}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <FileText className="w-4 h-4 text-red-500" /> {t("export")}{" "}
                    PDF
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {!selectionMode && (
          <div className="relative">
            <button
              onClick={() => setShowExportMenu((p) => !p)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-semibold text-xs transition"
            >
              <Download className="w-3.5 h-3.5" /> {t("exportAll")}
            </button>
            {showExportMenu && (
              <div className="absolute left-0 top-11 bg-white rounded-xl shadow-lg border border-gray-100 z-50 min-w-[140px] overflow-hidden">
                <button
                  onClick={exportToCSV}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FileText className="w-4 h-4 text-green-500" /> {t("export")}{" "}
                  CSV
                </button>
                <div className="h-px bg-gray-100" />
                <button
                  onClick={exportToPDF}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FileText className="w-4 h-4 text-red-500" /> {t("export")}{" "}
                  PDF
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invoices section */}
      <div className="px-4 mt-3 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900">{t("invoices")}</h2>
          <span className="bg-purple-50 text-purple-600 text-[10px] font-semibold px-2.5 py-1 rounded-xl">
            {totalInvoices} {t("invoices")}
          </span>
        </div>

        {loadingInvoices && (
          <div className="flex items-center justify-center py-8">
            <div className="w-7 h-7 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loadingInvoices && searchFilteredInvoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Receipt className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-sm font-semibold text-gray-500 mb-1 text-center">
              {searchQuery.trim()
                ? t("noInvoicesFound")
                : t("invoicesWillAppearHere")}
            </p>
            <p className="text-xs text-gray-400 text-center">
              {searchQuery.trim() ? t("searchInvoices") : t("createNewInvoice")}
            </p>
          </div>
        ) : (
          !loadingInvoices && (
            <div className="bg-white rounded-2xl p-3 shadow-sm space-y-2">
              {chunkArray(searchFilteredInvoices, 3).map((row, pIdx) => (
                <div key={pIdx} className="flex gap-2">
                  {row.map((invoice) => renderInvoiceCard(invoice))}
                  {row.length < 3 && Array.from({ length: 3 - row.length }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        flex: "1 1 calc(33.333% - 8px)",
                        maxWidth: "calc(33.333% - 8px)",
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          )
        )}

        {/* Invoices without dates */}
        {!loadingInvoices && searchFilteredWithoutDates.length > 0 && (
          <div className="bg-purple-50 rounded-2xl p-3 border-2 border-purple-200 space-y-2">
            <div className="flex items-center gap-1.5 mb-1">
              <AlertCircle className="w-4 h-4 text-purple-500" />
              <h3 className="text-xs font-bold text-purple-800">
                {t("needsAttention")} ({searchFilteredWithoutDates.length})
              </h3>
            </div>
            {chunkArray(searchFilteredWithoutDates, 3).map((row, pIdx) => (
              <div key={pIdx} className="flex gap-2">
                {row.map((invoice) => renderInvoiceCard(invoice))}
                {row.length < 3 && Array.from({ length: 3 - row.length }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      flex: "1 1 calc(33.333% - 8px)",
                      maxWidth: "calc(33.333% - 8px)",
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setCreateInvoiceVisible(true)}
        className={`fixed bottom-6 ${isRTL ? "left-6" : "right-6"} w-16 h-16 bg-purple-500 hover:bg-purple-600 rounded-full flex items-center justify-center shadow-2xl transition z-40`}
      >
        <Plus className="w-8 h-8 text-white" />
      </button>

      {/* Modals */}
      <BossCreateInvoice
        visible={createInvoiceVisible}
        onClose={() => setCreateInvoiceVisible(false)}
        onSuccess={() => {
          if (choosedMonth && userData?.id) {
            loadEmailInvoices(choosedMonth, userData.id);
            loadAllTimeInvoices(userData.id);
          }
        }}
      />

      <BossModalShowInvoice
        visible={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
      />

      {imageModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
          <button
            onClick={() => setImageModalVisible(false)}
            className="absolute top-10 right-5 z-10"
          >
            <X className="w-9 h-9 text-white" />
          </button>
          {selectedInvoiceImage && (
            <img
              src={selectedInvoiceImage}
              alt="Invoice"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
        </>
      )}
    </div>
  );
}
